import React from 'react';
import { useTranslation } from 'react-i18next';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import OutsideAlerter from 'shared/components/outside-alerter/outside-alerter';

import { MultiValueFilterChipProps, useMultiValueFilterChip } from './hooks/index';

import './multi-value-filter-chip.scss';

const MultiValueFilterChip = ({
  options,
  onOptionsSelected,
  placeHolder,
  selectedOptions,
  disabled,
}: Readonly<MultiValueFilterChipProps>): React.JSX.Element => {
  const { t } = useTranslation('multiValueFilterChip');

  const { isOpen, close, toggleIsOpen, handleOnCheckboxChanged, getLabel, sortedOptions } = useMultiValueFilterChip({
    options,
    onOptionsSelected,
    placeHolder,
    selectedOptions,
  });

  const renderFilterOptions = sortedOptions.map((option) => {
    const isSelected = selectedOptions.some((o) => o.key === option.key);
    return (
      <MenuItem
        data-testid={`filterOption${option.key.toString().replaceAll(/\s+/g, '_')}`}
        onClick={() => handleOnCheckboxChanged(option)}
        key={option.key}
        sx={{ padding: '0px 4px' }}
        disabled={disabled}
      >
        <Checkbox
          sx={{
            padding: 0,
            margin: '8px',
          }}
          size="small"
          onChange={() => handleOnCheckboxChanged(option)}
          checked={isSelected}
          key={option.key}
          disabled={disabled}
        />
        {option.label}
      </MenuItem>
    );
  });

  return (
    <OutsideAlerter funcDelegate={close}>
      <div className="multi-value-filter-chip">
        <Chip
          data-testid={`filterChip${placeHolder}`}
          label={getLabel()}
          variant="outlined"
          clickable
          deleteIcon={<ArrowDropDownIcon />}
          onDelete={toggleIsOpen}
          onClick={toggleIsOpen}
          className={selectedOptions.length > 0 ? 'active' : ''}
          disabled={disabled}
        />
        {isOpen && (
          <Paper elevation={1} className="options">
            {sortedOptions.length === 0 ? (
              <Typography className="no-filter-options">{t('noFilterOptions')}</Typography>
            ) : (
              renderFilterOptions
            )}
          </Paper>
        )}
      </div>
    </OutsideAlerter>
  );
};

export default MultiValueFilterChip;
